import React, { useState } from 'react';
import {
  Home,
  BarChart3,
  Search,
  Users,
  Mail,
  Link2,
  Calendar,
  CalendarDays,
  Webhook,
  CreditCard,
  Shield,
  Settings,
  Key,
  LogOut,
  Menu,
  X,
  Building2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import styles from './AppShell.module.css';

const CORE_ITEMS = [
  { id: 'home',    label: 'Home',        icon: Home },
  { id: 'matches', label: 'Matches',     icon: Search,  badgeKey: 'matches' },
  // TODO(naming) primary "activity" nav item label
  { id: 'activity', label: 'Activity',   icon: Mail,    badgeKey: 'activity' },
  { id: 'network', label: 'Network',     icon: Users },
];

const TOOLS_ITEMS = [
  { id: 'venn-links', label: 'Venn Links', icon: Link2 },
  { id: 'events',     label: 'Events',     icon: CalendarDays },
  // 'analytics' nav item hidden — page is dead until rewrite per separate audit.
  // BusinessAnalytics + analyticsService preserved on disk as the rewrite's starting point.
  { id: 'calendar',   label: 'Calendar',   icon: Calendar },
];

const SETTINGS_ITEMS = [
  { id: 'profile',     label: 'Profile',   icon: Settings },
  { id: 'payments',    label: 'Payments',  icon: CreditCard },
  { id: 'webhooks',    label: 'Webhooks',  icon: Webhook },
  { id: 'permissions', label: 'Access',    icon: Shield },
  { id: 'api-keys',    label: 'API Keys',  icon: Key },
  { id: 'billing',     label: 'Billing',   icon: CreditCard },
];

function NavItem({ item, activeTab, badges, onClick, styles: s }) {
  const Icon = item.icon;
  const isActive = activeTab === item.id;
  const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;

  return (
    <button
      className={`${s.navItem} ${isActive ? s.navItemActive : ''}`}
      onClick={() => onClick(item.id)}
    >
      <Icon size={18} className={s.navIcon} />
      <span className={s.navLabel}>{item.label}</span>
      {badgeCount > 0 && (
        <span className={s.navBadge}>{badgeCount}</span>
      )}
    </button>
  );
}

export default function AppShell({
  businessName,
  userEmail,
  userName,
  activeTab,
  onTabChange,
  onSignOut,
  badges = {},
  children
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const initials = userName
    ? userName.charAt(0).toUpperCase()
    : userEmail
      ? userEmail.charAt(0).toUpperCase()
      : '?';

  const handleNavClick = (id) => {
    if (id === 'api-keys') {
      window.location.href = '/api-keys';
      return;
    }
    if (id === 'billing') {
      window.location.href = '/billing';
      return;
    }
    if (id === 'events') {
      window.location.href = '/events';
      return;
    }
    onTabChange(id);
    setSidebarOpen(false);
  };

  // Auto-expand settings if active tab is in settings group
  const isSettingsTab = SETTINGS_ITEMS.some(i => i.id === activeTab);
  const showSettings = settingsOpen || isSettingsTab;

  return (
    <div className={styles.shell}>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${sidebarOpen ? styles.backdropVisible : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarBrand}>
          <div className={styles.brandIcon}>
            <Building2 size={20} />
          </div>
          <h2 className={styles.brandName}>{businessName || 'Vennio'}</h2>
        </div>

        <nav className={styles.nav}>
          {/* Core */}
          {CORE_ITEMS.map(item => (
            <NavItem key={item.id} item={item} activeTab={activeTab} badges={badges} onClick={handleNavClick} styles={styles} />
          ))}

          {/* Tools */}
          <div className={styles.navDivider} />
          <span className={styles.navGroupLabel}>Tools</span>
          {TOOLS_ITEMS.map(item => (
            <NavItem key={item.id} item={item} activeTab={activeTab} badges={badges} onClick={handleNavClick} styles={styles} />
          ))}

          {/* Settings (collapsible) */}
          <div className={styles.navDivider} />
          <button
            className={styles.navGroupToggle}
            onClick={() => setSettingsOpen(prev => !prev)}
          >
            <span className={styles.navGroupLabel}>Settings</span>
            {showSettings
              ? <ChevronDown size={14} className={styles.navGroupChevron} />
              : <ChevronRight size={14} className={styles.navGroupChevron} />
            }
          </button>
          {showSettings && SETTINGS_ITEMS.map(item => (
            <NavItem key={item.id} item={item} activeTab={activeTab} badges={badges} onClick={handleNavClick} styles={styles} />
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <button className={styles.footerBtn} onClick={onSignOut}>
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Top bar */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <button
            className={styles.hamburger}
            onClick={() => setSidebarOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <div className={styles.topbarRight}>
          <span className={styles.topbarEmail}>{userEmail}</span>
          <div className={styles.avatar}>{initials}</div>
        </div>
      </header>

      {/* Content */}
      <main className={styles.content}>
        {children}
      </main>
    </div>
  );
}
